import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { RequireAuthContainer } from "../../components/containers/RequireAuthContainer"
import { DashboardLayout } from "../../components/DashboardLayout"
import { JobList } from "../../components/JobList"
import { useAuth } from "../../hooks/useAuth"
import { useToast } from "@/hooks/use-toast"
import apiClient from "../../api/apiClient"
import { Job } from "../../shared-types"

/**
 * 🚛 DriverDashboard
 * Shows assigned jobs to a logged-in driver. Guards against role mismatch.
 * Handles job cancellation. Auto-fetches jobs on mount.
 */
export default function DriverDashboard() {
  // 🧭 Auth & navigation context
  const { user, role } = useAuth()
  const navigate = useNavigate()
  const { toast } = useToast()

  // 📊 Jobs state
  const [jobs, setJobs] = useState<Job[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  // 🔒 Kick out users that aren't drivers
  useEffect(() => {
    if (role && role !== "driver") {
      navigate(`/${role}/dashboard`)
    }
  }, [role, navigate])

  // 📥 Fetch driver jobs on load
  useEffect(() => {
    if (user?.id) fetchJobs(user.id)
  }, [user])

  // 🎣 API call to fetch driver's jobs
  const fetchJobs = async (userId: string) => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await apiClient.getDriverJobs(userId)
      if (response?.data) setJobs(response.data)
      if (response?.error) throw response.error
    } catch (err) {
      setError(err as Error)
      toast({
        title: "Error loading jobs",
        description: (err as Error).message,
        variant: "error"
      })
    } finally {
      setIsLoading(false)
    }
  }

  // ❌ Cancel a job
  const handleCancelJob = async (jobId: string) => {
    try {
      const result = await apiClient.cancelJob(jobId)
      if (result?.error) throw result.error

      setJobs(prev =>
        prev.map(job =>
          job.id === jobId ? { ...job, status: "cancelled" } : job
        )
      )

      toast({
        title: "Job Cancelled",
        description: "The job has been cancelled",
        variant: "default"
      })
    } catch (err) {
      toast({
        title: "Error",
        description: (err as Error).message,
        variant: "error"
      })
    }
  }

  return (
    <RequireAuthContainer>
      <DashboardLayout>
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Driver Dashboard
          </h1>

          <JobList
            jobs={jobs}
            isLoading={isLoading}
            error={error}
            onCancelJob={handleCancelJob}
            onAssignJob={() => {}}
            emptyMessage="No jobs assigned yet."
          />
        </div>
      </DashboardLayout>
    </RequireAuthContainer>
  )
}
